import {
  ComputedRoute,
  PrintableBikeIcon,
  PrintableTrainIcon,
  PrintableWalkIcon,
  Route,
  instructionDirectionsMap,
  instructionFacilitiesMap,
  instructionOrientationsMap,
  useUnits,
} from '@geovelo-frontends/commons';
import { useTheme } from '@mui/material/styles';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const styles = StyleSheet.create({
  wrapper: {
    marginLeft: 50,
    marginTop: 10,
  },
  header: {
    alignItems: 'center',
    border: '1px solid #ddd',
    borderRadius: 5,
    flexDirection: 'row',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  icon: {
    flexShrink: 0,
    height: 20,
    width: 20,
  },
  headerTextWrapper: {
    marginLeft: 30,
    marginRight: 30,
  },
  headerTitle: {
    fontSize: 12,
  },
  headerSubtitle: {},
  list: {
    marginTop: 10,
  },
  listItem: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: 5,
    minHeight: 15,
  },
  listItemIcon: {
    flexShrink: 0,
    height: 15,
    width: 15,
  },
  listItemTextWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    marginLeft: 30,
    marginRight: 30,
  },
  listItemText: {
    fontSize: 8,
  },
  listItemDividerWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: 5,
  },
  listItemDividerText: {
    flexShrink: 0,
    fontSize: 8,
  },
  listItemDivider: {
    borderTop: '1px solid #ddd',
    flexGrow: 1,
    marginLeft: 5,
  },
});

interface IProps {
  route: ComputedRoute | Route;
  sectionIndex: number;
}

function SectionCard({ route: { sections }, sectionIndex }: IProps): JSX.Element {
  const { t } = useTranslation();
  const {
    palette: { primary },
  } = useTheme();
  const { toTime, toDistance } = useUnits();

  const section = sections[sectionIndex];
  const { transportMode, direction, duration, distance } = section;
  const instructions =
    ('instructions' in section &&
      section.instructions.filter(
        ({ direction }) => direction !== 'reachedYourDestination' && direction !== 'takeSharedBike',
      )) ||
    undefined;

  return (
    <View style={styles.wrapper}>
      <View style={styles.header} wrap={false}>
        <>
          {transportMode === 'bike' ? (
            <PrintableBikeIcon color={primary.main} height={20} style={styles.icon} width={20} />
          ) : transportMode === 'pedestrian' ? (
            <PrintableWalkIcon color={primary.main} height={20} style={styles.icon} width={20} />
          ) : (
            <PrintableTrainIcon color={primary.main} height={20} style={styles.icon} width={20} />
          )}
        </>
        <View style={styles.headerTextWrapper}>
          <Text style={styles.headerTitle}>
            {t('commons.routing.roadmap.via_direction', { direction })}
          </Text>
          <Text style={styles.headerSubtitle}>
            {toTime(duration)} &bull; {toDistance(distance)}
          </Text>
        </View>
      </View>
      {(transportMode === 'bike' || transportMode === 'pedestrian') &&
        instructions &&
        instructions.length > 0 && (
          <>
            {instructions && instructions.length > 0 && (
              <View style={styles.list}>
                {instructions.map(
                  (
                    {
                      orientation: orientationKey,
                      direction: directionKey,
                      roadName,
                      facility: facilityKey,
                      distance,
                    },
                    index,
                  ) => {
                    const direction = instructionDirectionsMap[directionKey];

                    const { labelKey } = instructionOrientationsMap[orientationKey];
                    const DirectionIcon = direction?.PrintableIcon;

                    const facility = instructionFacilitiesMap[facilityKey];
                    const FacilityIcon = facility?.PrintableIcon;

                    return (
                      <Fragment key={index}>
                        <View style={styles.listItem} wrap={false}>
                          {DirectionIcon ? (
                            <DirectionIcon
                              color={primary.main}
                              height={20}
                              style={styles.listItemIcon}
                              width={20}
                            />
                          ) : (
                            <View style={{ width: 20 }} />
                          )}
                          <View style={styles.listItemTextWrapper}>
                            <Text style={styles.listItemText}>
                              {direction &&
                                t(direction.labelKey, {
                                  orientation: t(labelKey),
                                  roadName:
                                    roadName ||
                                    (facility ? t(facility.labelKey).toLowerCase() : ''),
                                  context: roadName || facility ? 'detailed' : '',
                                })}
                            </Text>
                          </View>
                          {facility && FacilityIcon && (
                            <FacilityIcon
                              color="#333"
                              height={20}
                              style={styles.listItemIcon}
                              width={20}
                            />
                          )}
                        </View>
                        {index < instructions.length - 1 && (
                          <View style={styles.listItemDividerWrapper}>
                            <Text style={styles.listItemDividerText}>
                              {toDistance(distance || 0)}
                            </Text>
                            <View style={styles.listItemDivider} />
                          </View>
                        )}
                      </Fragment>
                    );
                  },
                )}
              </View>
            )}
          </>
        )}
    </View>
  );
}

export default SectionCard;
